import React, { useRef, useState } from "react";
import { toast } from "sonner";
import { ImageSquare, Spinner } from "@phosphor-icons/react";
import MarkdownToolbar from "@/components/MarkdownToolbar";
import { uploadImage, IMAGE_ACCEPT } from "@/lib/uploadImage";
import { Field, ImageGuidelines } from "./Field";

const CONTENT_GUIDELINES = [
    "Aspect ratio: 3:2 or 4:3 (landscape) works best inside the article column.",
    "Recommended size: 1200 × 800 px (min width 900 px). Maximum file size: 5 MB.",
    "Format: JPG, PNG, WebP, GIF, or HEIC (iPhone). HEIC is auto-converted to JPG. Place images on their own line for full-width rendering.",
];

export const ContentField = ({ value, onChange }) => {
    const contentRef = useRef(null);
    const inlineInputRef = useRef(null);
    const [uploading, setUploading] = useState(false);

    const onInlineFile = async (file) => {
        if (!file) return;
        setUploading(true);
        try {
            const url = await uploadImage(file);
            const token = `\n\n![](${url})\n\n`;
            const ta = contentRef.current;
            if (ta) {
                const start = ta.selectionStart ?? value.length;
                const end = ta.selectionEnd ?? value.length;
                onChange(value.slice(0, start) + token + value.slice(end));
                // Restore cursor after the inserted token.
                requestAnimationFrame(() => {
                    ta.focus();
                    const caret = start + token.length;
                    ta.setSelectionRange(caret, caret);
                });
            } else {
                onChange(value + token);
            }
            toast.success("Image inserted into content");
        } catch (e) {
            toast.error(e?.response?.data?.detail || e.message || "Upload failed");
        } finally {
            setUploading(false);
            if (inlineInputRef.current) inlineInputRef.current.value = "";
        }
    };

    const onKeyDown = (e) => {
        const ctrl = e.metaKey || e.ctrlKey;
        if (!ctrl) return;
        const ta = contentRef.current;
        if (!ta) return;
        const wrapSel = (mark) => {
            const start = ta.selectionStart;
            const end = ta.selectionEnd;
            const sel = value.slice(start, end) || "text";
            onChange(value.slice(0, start) + mark + sel + mark + value.slice(end));
            requestAnimationFrame(() => {
                ta.focus();
                ta.setSelectionRange(start + mark.length, start + mark.length + sel.length);
            });
        };
        if (e.key.toLowerCase() === "b") { e.preventDefault(); wrapSel("**"); }
        else if (e.key.toLowerCase() === "i") { e.preventDefault(); wrapSel("*"); }
    };

    return (
        <Field label="Content">
            <MarkdownToolbar textareaRef={contentRef} value={value} onChange={onChange} />
            <textarea
                ref={contentRef}
                data-testid="editor-content"
                required
                value={value}
                rows={14}
                onChange={(e) => onChange(e.target.value)}
                onKeyDown={onKeyDown}
                className="tz-input font-mono text-sm"
                placeholder="Write the article. Use the toolbar above for **bold**, *italic*, lists, headings, and links. Paragraphs are split by a blank line."
            />
            <input
                ref={inlineInputRef}
                type="file"
                accept={IMAGE_ACCEPT}
                className="hidden"
                data-testid="editor-inline-input"
                onChange={(e) => onInlineFile(e.target.files?.[0])}
            />
            <div className="mt-3 flex flex-wrap items-center gap-3">
                <button
                    type="button"
                    onClick={() => inlineInputRef.current?.click()}
                    disabled={uploading}
                    data-testid="editor-insert-image"
                    className="btn-secondary inline-flex items-center gap-2 text-xs disabled:opacity-60"
                >
                    {uploading ? <Spinner size={14} className="animate-spin" /> : <ImageSquare size={14} weight="bold" />}
                    {uploading ? "Uploading…" : "Insert image into content"}
                </button>
                <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500">
                    Inserts at cursor as <code className="text-zinc-300">![](url)</code>
                </span>
            </div>
            <ImageGuidelines title="In-content image guidelines" lines={CONTENT_GUIDELINES} />
        </Field>
    );
};
